import { Country } from "../types/country";
import {
  HkSubDistrict,
  PickupSechuleSettingsForm,
  SfDistrict,
  SfLocation,
  ShipSettingForm,
  ShipSettingReturn,
} from "../types/shipSetting";
import { get, post } from "./fetch.core";

function normalizePickupSettings(raw: PickupSechuleSettingsForm | undefined): {
  scheduleType: "fixed" | "relative";
  schedule: { from: string; to: string };
  excludeWeekDays: number[];
  excludeDates: string[];
  pickupAddrIds: string[];
} {
  const ps = raw ?? ({} as PickupSechuleSettingsForm);

  let ids: unknown = ps.pickupAddrIds as unknown;
  if (!Array.isArray(ids)) {
    ids = ids === false || ids === undefined || ids === null ? [] : [ids];
  }
  const pickupAddrIds = (ids as unknown[])
    .filter((x) => x !== false && x !== true)
    .map(String)
    .filter((id) => /^[a-f\d]{24}$/i.test(id));

  const scheduleType: "fixed" | "relative" =
    ps.scheduleType === "fixed" || ps.scheduleType === "relative" ? ps.scheduleType : "relative";

  const from = ps.schedule?.from != null && String(ps.schedule.from) !== "" ? String(ps.schedule.from) : "0";
  const to = ps.schedule?.to != null && String(ps.schedule.to) !== "" ? String(ps.schedule.to) : "0";

  const excludeWeekDays = Array.isArray(ps.excludeWeekDays)
    ? ps.excludeWeekDays.map((n) => Number(n)).filter((n) => !Number.isNaN(n) && n >= 0 && n <= 6)
    : [];

  const excludeDates = Array.isArray(ps.excludeDates) ? ps.excludeDates.map(String) : [];

  return {
    scheduleType,
    schedule: { from, to },
    excludeWeekDays,
    excludeDates,
    pickupAddrIds,
  };
}

async function getCountryList() {
  const res = await get<Country[]>("/admin/shipSetting/countryList");
  return res.data;
}

async function addShipSetting(data: ShipSettingForm) {
  delete data.id;
  const sanitizedData = sanitizeShipSettingData(data);
  await post("/admin/shipSetting/add", sanitizedData);
}

async function updateShipSetting(data: ShipSettingForm) {
  const sanitizedData = sanitizeShipSettingData(data);
  await post("/admin/shipSetting/update", sanitizedData);
}

async function deleteShipSetting(id: string) {
  await post("/admin/shipSetting/delete", { id });
}

async function getShipSettings() {
  const res = await get<ShipSettingReturn[]>("/admin/shipSetting/list");
  return res.data;
}

async function getShipSetting(id: string): Promise<ShipSettingForm> {
  const res = await get<ShipSettingReturn>(`/admin/shipSetting/single?id=${id}`);
  return convertReturnToForm(id, res.data);
}

function sanitizeShipSettingData(data: ShipSettingForm) {
  return {
    ...data,
    options: data.options.map((d) => {
      if (!d.productRestriction.type || d.productRestriction.type === "none") {
        delete d.productRestriction.type;
        delete d.productRestriction.restrictToIds;
      }

      if (!d.categoryRestriction.type || d.categoryRestriction.type === "none") {
        delete d.categoryRestriction.type;
        delete d.categoryRestriction.restrictToIds;
      }
      const feeOptions = d.feeOptions.map((o) => {
        if (!o.productRestriction?.type || o.productRestriction.type === "none") {
          delete o.productRestriction?.type;
          delete o.productRestriction?.restrictToIds;
        }

        if (!o.categoryRestriction?.type || o.categoryRestriction.type === "none") {
          delete o.categoryRestriction?.type;
          delete o.categoryRestriction?.restrictToIds;
        }

        return o;
      });

      if (d.shipType !== "pickup") {
        return {
          ...d,
          feeOptions,
          pickupType: undefined,
          pickupSettings: undefined,
        };
      }

      return {
        ...d,
        feeOptions,
        pickupSettings: normalizePickupSettings(d.pickupSettings),
      };
    }),
  };
}

function convertReturnToForm(id: string, shipSetting: ShipSettingReturn): ShipSettingForm {
  return {
    id,
    countryIds: shipSetting.countryList.map((c) => c.id),
    options: shipSetting.options,
  };
}

async function getHkSubDistricts() {
  const res = await get<HkSubDistrict[]>("/admin/shipSetting/hkSubdistricts");
  return res.data;
}

async function getSfSubDistricts() {
  const res = await get<SfDistrict[]>("/public/ship/sfSubDistricts");
  return res.data;
}

async function getSfLocations(subDistrict: string) {
  const res = await get<SfLocation[]>(`/public/ship/sfLocations?subDistrict=${subDistrict}`);
  return res.data;
}

const obj = {
  getCountryList,
  addShipSetting,
  getShipSettings,
  getShipSetting,
  updateShipSetting,
  deleteShipSetting,
  getHkSubDistricts,
  getSfSubDistricts,
  getSfLocations,
};

export default obj;
